import {
  AlarmClock, Archive, ArchiveX, ArrowRight, BadgeCheck, Ban, BookCheck,
  CalendarClock, CalendarX2, Check, CheckCheck, Circle, CircleCheck,
  CirclePause, CircleX, Clock, ExternalLink, Eye, FilePlus2, FileX2, Gavel,
  HandCoins, Handshake, Hourglass, Inbox, Pause, PenLine, PencilLine,
  PieChart, Scale, Send, ShieldAlert, Signature, TriangleAlert, Undo2,
  UserSearch, X, type LucideIcon,
} from "lucide-react";

/**
 * Status pills: soft, meaning-coded tones.
 *
 * Every status maps to one of seven meanings, and each meaning has a single
 * tone: a 10% tint, deep text that clears 4.5:1 on that tint, a hairline ring
 * of the same hue, and a Dark-theme variant. Solid saturated fills with white
 * text read as alarms everywhere; the tint keeps the colour as a signal while
 * the pill stays quiet. `dot` is the solid swatch for the few places that show
 * a status as a small dot rather than a pill.
 *
 *   neutral   not started, or no longer live    slate
 *   info      moving through a process          sky
 *   attention waiting on someone / needs a nudge amber
 *   decision  awaiting signature or disposition indigo
 *   success   done and good                     emerald
 *   danger    failed, lapsed or breached        rose
 *   escalated moved to legal action             violet
 */
export interface StatusTone {
  bg: string;
  text: string;
  dot: string;
}

// Class names are written out in full so Tailwind can see them.
export const TONES = {
  neutral: {
    bg: "bg-slate-500/10 ring-1 ring-inset ring-slate-600/20 dark:bg-slate-400/15 dark:ring-slate-400/25",
    text: "text-slate-700 dark:text-slate-300",
    dot: "bg-slate-500",
  },
  info: {
    bg: "bg-sky-500/10 ring-1 ring-inset ring-sky-600/20 dark:bg-sky-400/15 dark:ring-sky-400/25",
    text: "text-sky-800 dark:text-sky-300",
    dot: "bg-sky-500",
  },
  attention: {
    bg: "bg-amber-500/10 ring-1 ring-inset ring-amber-600/25 dark:bg-amber-400/15 dark:ring-amber-400/25",
    text: "text-amber-800 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  decision: {
    bg: "bg-indigo-500/10 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-400/15 dark:ring-indigo-400/25",
    text: "text-indigo-700 dark:text-indigo-300",
    dot: "bg-indigo-500",
  },
  success: {
    bg: "bg-emerald-500/10 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-400/15 dark:ring-emerald-400/25",
    text: "text-emerald-800 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
  danger: {
    bg: "bg-rose-500/10 ring-1 ring-inset ring-rose-600/20 dark:bg-rose-400/15 dark:ring-rose-400/25",
    text: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500",
  },
  escalated: {
    bg: "bg-violet-500/10 ring-1 ring-inset ring-violet-600/20 dark:bg-violet-400/15 dark:ring-violet-400/25",
    text: "text-violet-700 dark:text-violet-300",
    dot: "bg-violet-500",
  },
} satisfies Record<string, StatusTone>;

/** Fallback for a label with no entry: neutral, never an unstyled pill. */
export const UNKNOWN_STATUS: StatusTone = TONES.neutral;

export const STATUS_COLORS: Record<string, StatusTone> = {
  // Trucks
  Assigned: TONES.info,
  "Maintenance Due": TONES.attention,
  Stuck: TONES.attention,
  "Broken Down": TONES.danger,
  Maintenance: TONES.attention,

  // Drivers
  Available: TONES.success,
  Unavailable: TONES.danger,

  // Orders
  None: TONES.neutral,
  Pending: TONES.attention,
  Confirmed: TONES.info,
  "In Production": TONES.info,
  "Quality Control": TONES.info,
  Ready: TONES.success,
  Dispatched: TONES.info,
  Delivered: TONES.success,
  Completed: TONES.success,

  // Customers
  Active: TONES.success,
  Inactive: TONES.neutral,
  // A legacy (scanned, pre-LawSigna) contract.
  Legacy: TONES.attention,

  // Products
  "In Stock": TONES.success,
  "Out of Stock": TONES.danger,

  // Productions
  Production: TONES.attention,
  "Qc pending": TONES.info,

  // Inventory
  OK: TONES.success,
  CRITICAL: TONES.attention,

  // Quality Control
  "Qc Required": TONES.attention,
  InProgress: TONES.info,
  Passed: TONES.success,
  Failed: TONES.danger,

  // Billing
  Paid: TONES.success,

  // Pending: { bg: "bg-amber-700", text: "text-white" },

  Approved: TONES.success,

  Rejected: TONES.danger,

  // Contracts
  Draft: TONES.neutral,
  Created: TONES.neutral,
  "In Editing": TONES.attention,
  "Waiting Approval": TONES.info,
  "Pending Signature": TONES.decision,
  Signed: TONES.success,
  Expired: TONES.danger,
  Terminated: TONES.danger,
  Archived: TONES.neutral,
  // Workflow send/receive statuses (ContractStatus 12-17) — previously had
  // no entries here at all, so the status chip rendered with no background
  // for any contract in one of these states, same root cause as the
  // "In Editing" rename above (STATUS_COLORS is keyed by label text, so a
  // label with no matching key here silently gets undefined bg/text).
  "Sent For Approval": TONES.info,
  "Sent To Reviewer": TONES.attention,
  "Sent For External Review": TONES.info,
  "External Review Received": TONES.info,
  Reviewed: TONES.success,
  "Sent To Repository": TONES.success,

  // Obligations and receivables (dashboard drill-down tables).
  Upcoming: TONES.info,
  "Non-Compliant": TONES.danger,
  Overdue: TONES.danger,
  Settled: TONES.success,

  // Receivables Recovery entry statuses (ENTRY_STATUS_META labels in
  // features/receivables/receivableEngine.ts). Draft, Approved, Active and
  // Rejected already have colours above and keep them.
  "Pending Editor Review": TONES.info,
  "Returned by Editor": TONES.attention,
  "Pending Approval": TONES.attention,
  "Returned for Changes": TONES.attention,
  Paused: TONES.attention,
  "Partially Recovered": TONES.success,
  "Pending Disposition": TONES.decision,
  "Disposed – Write Off": TONES.neutral,
  "Disposed – Fully Recovered": TONES.success,
  "Disposed – Settlement": TONES.success,
  "Disposed – Case Created": TONES.escalated,
  Cancelled: TONES.neutral,

  // Clause Library statuses (clauseStatusMeta in components/clauseLibrary/clauseMeta.ts).
  // Draft already has its colour above.
  "In Review": TONES.info,
  Published: TONES.success,
  Retired: TONES.neutral,
};

/* ---------- Status icons ----------
   Each pill leads with an icon in a small tinted circle (PillIcon). A status
   names its own icon where one says it best; anything else falls back to
   its tone's icon, so every pill has one and a new status needs no entry. */
export type ToneName = keyof typeof TONES;

export const TONE_ICONS: Record<ToneName, LucideIcon> = {
  neutral: Circle,
  info: ArrowRight,
  attention: Clock,
  decision: PenLine,
  success: Check,
  danger: TriangleAlert,
  escalated: Gavel,
};

export const STATUS_ICONS: Record<string, LucideIcon> = {
  // Contracts
  None: Circle,
  Draft: PencilLine,
  Created: FilePlus2,
  "In Editing": PencilLine,
  "Waiting Approval": Hourglass,
  "Sent For Approval": Send,
  "Sent To Reviewer": UserSearch,
  "Sent For External Review": ExternalLink,
  "External Review Received": Inbox,
  Reviewed: CheckCheck,
  "Pending Signature": Signature,
  Signed: BadgeCheck,
  "Sent To Repository": Archive,
  Expired: CalendarX2,
  Terminated: Ban,
  Archived: Archive,
  // Shared lifecycle
  Active: CircleCheck,
  Inactive: CirclePause,
  Legacy: Archive,
  Approved: Check,
  Rejected: X,
  Pending: Clock,
  Completed: CheckCheck,
  Cancelled: CircleX,
  // Clause library
  "In Review": Eye,
  Published: BookCheck,
  Retired: ArchiveX,
  // Obligations and receivables
  Upcoming: CalendarClock,
  Overdue: AlarmClock,
  "Non-Compliant": ShieldAlert,
  Settled: HandCoins,
  Paid: HandCoins,
  "Pending Editor Review": Eye,
  "Returned by Editor": Undo2,
  "Pending Approval": Hourglass,
  "Returned for Changes": Undo2,
  Paused: Pause,
  "Partially Recovered": PieChart,
  "Pending Disposition": Scale,
  "Disposed – Write Off": FileX2,
  "Disposed – Fully Recovered": HandCoins,
  "Disposed – Settlement": Handshake,
  "Disposed – Case Created": Gavel,
};

/** The tone a STATUS_COLORS entry uses (entries are TONES objects). */
export const toneNameOf = (tone: StatusTone | undefined): ToneName => {
  const hit = (Object.keys(TONES) as ToneName[]).find((k) => TONES[k] === tone);
  return hit ?? "neutral";
};

/** The icon for an English status key: its own, else its tone's. */
export const getStatusIcon = (englishKey: string): LucideIcon =>
  STATUS_ICONS[englishKey] ?? TONE_ICONS[toneNameOf(STATUS_COLORS[englishKey])];

/** Spread into a FilterOption so a chip carries its status tone and icon
 *  regardless of the (possibly translated) label it shows. */
export const statusToneOption = (englishKey: string) => ({
  tone: STATUS_COLORS[englishKey] ?? TONES.neutral,
  toneIcon: getStatusIcon(englishKey),
});
